'use client';

import { type PropsWithChildren, useRef } from 'react';

export default function ConfettiHover({ children }: PropsWithChildren) {
  const confettiTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const createConfetti = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const colors = ['#a855f7', '#3b82f6', '#ec4899', '#f59e0b', '#10b981'];
    const count = 30;
    for (let i = 0; i < count; i += 1) {
      const confetti = document.createElement('div');
      const angle = (Math.PI * 2 * i) / count;
      const distance = 100 + Math.random() * 100;
      confetti.style.cssText = `
        position: fixed;
        width: 8px;
        height: 8px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        left: ${x}px;
        top: ${y}px;
        border-radius: 50%;
        pointer-events: none;
        z-index: 9999;
      `;
      document.body.appendChild(confetti);
      const animation = confetti.animate(
        [
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          {
            transform: `translate(calc(-50% + ${Math.cos(angle) * distance}px), calc(-50% + ${
              Math.sin(angle) * distance - 30
            }px)) scale(1)`,
            opacity: 0,
          },
        ],
        { duration: 1000, easing: 'ease-out', fill: 'forwards' },
      );
      animation.onfinish = () => confetti.remove();
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (window.innerWidth < 768) return;
    if (confettiTimeoutRef.current) clearTimeout(confettiTimeoutRef.current);
    const element = e.currentTarget;
    confettiTimeoutRef.current = setTimeout(() => {
      createConfetti(element);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (confettiTimeoutRef.current) {
      clearTimeout(confettiTimeoutRef.current);
      confettiTimeoutRef.current = null;
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover-only confetti is decorative; no keyboard interaction is lost
    <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className='inline'>
      {children}
    </span>
  );
}
